import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area, Legend, Brush
} from "recharts";
import { Select, Modal } from "antd";
import '../../styles/Imu.css';
import { useLoading } from "../../contexts/LoadingContext";
import VibrazioneIcon from "../../assets/icons/Vibrazione.svg";
import { fetchVibrationData, fetchIMUData, fetchIMUIndex } from './APIs/sensorApi';
import { ExpandButton } from '../../components';

// Constants
const TIME_TICKS = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59'];

// Axis configuration
const AXIS_CONFIG = [
  { id: 'total', label: 'Totale (√(X²+Y²+Z²))', color: '#8C74FF', bgColor: '#F0EDFF' },
  { id: 'x', label: 'Asse X', color: '#FF4D4D', bgColor: '#FFE6E6' },
  { id: 'y', label: 'Asse Y', color: '#4ECDC4', bgColor: '#E6F9F7' },
  { id: 'z', label: 'Asse Z', color: '#FFE100', bgColor: '#FFFBE6' }
];

const IMU = ({ arco, selectedDate: parentSelectedDate, radarSelected }) => {
  const [imuDates, setImuDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Handle array or string input for backward compatibility
    if (Array.isArray(parentSelectedDate)) {
      return parentSelectedDate.length > 0 ? parentSelectedDate[0] : null;
    }
    return parentSelectedDate || null;
  });
  const [imuData, setImuData] = useState([]);
  const [selectedRoadIndex, setSelectedRoadIndex] = useState(0);
  const [selectedAxes, setSelectedAxes] = useState(['total']); // Array of selected axes: 'total', 'x', 'y', 'z'
  const [isExpanded, setIsExpanded] = useState(false);
  const [useNewFormat, setUseNewFormat] = useState(true);
  const { showLoader, hideLoader } = useLoading();
  const [loadingData, setLoadingData] = useState(false);

  // Toggle axis selection
  const toggleAxis = (axis) => {
    setSelectedAxes(prev => {
      if (prev.includes(axis)) {
        // Remove if already selected (but keep at least one)
        return prev.length > 1 ? prev.filter(a => a !== axis) : prev;
      } else {
        // Add if not selected
        return [...prev, axis];
      }
    });
  };

  useEffect(() => {
    setLoadingData(true);
    showLoader("Caricamento dati vibrazione...");
    // Try new format first using centralized API service
    fetchVibrationData()
      .then((data) => {
        setUseNewFormat(true);
        // Extract unique dates from new format
        const allDates = [...new Set(data.map(item => {
          const timestamp = new Date(parseInt(item.time) * 1000);
          return timestamp.toISOString().split('T')[0];
        }))].sort();

        // Filter dates to show only 01/08/2024 - 09/08/2024
        const startDate = '2024-08-01';
        const endDate = '2024-08-09';
        const dates = allDates.filter(date => date >= startDate && date <= endDate);

        const dateOptions = dates.map(date => ({
          label: date,
          value: date
        }));

        setImuDates(dateOptions);

        // Set first date as default
        if (dateOptions.length > 0) {
          const firstDate = dateOptions[0]; // Use first date as default
          setSelectedDate(firstDate);
        }
        setLoadingData(false);
        hideLoader();
      })
      .catch(() => {
        // Fallback to old format
        console.warn("Failed to load Vibration_1min.json, falling back to imu_json");
        setUseNewFormat(false);
        fetchIMUIndex()
          .then((data) => {
            const byDate = {};
            data.forEach(item => {
              const date = item.start.split("_")[0];
              if (!byDate[date]) byDate[date] = [];
              byDate[date].push(item);
            });

            const sorted = Object.keys(byDate).sort();
            const dateOptions = sorted.map(date => ({
              label: date,
              value: date,
              files: byDate[date]
            }));

            setImuDates(dateOptions);

            // Set FIRST date as default
            if (dateOptions.length > 0) {
              setSelectedDate(dateOptions[0].value);
            }
            setLoadingData(false);
            hideLoader();
          })
          .catch(err => {
            console.error("Errore nel caricamento delle date IMU:", err);
            setLoadingData(false);
            hideLoader();
          });
      });
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    if (useNewFormat) {
      // Load and process new format data using centralized API service
      fetchVibrationData()
        .then((allData) => {
          // Filter data for selected date
          const filteredData = allData.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === selectedDate;
          });

          // Process data at 1-minute intervals
          const minuteData = filteredData.map((item) => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            const A_x = parseFloat(item['RMS(ax) [g]']) || 0;
            const A_y = parseFloat(item['RMS(ay) [g]']) || 0;
            const A_z = parseFloat(item['RMS(az) [g]']) || 0;
            const A_tot = parseFloat(item['RMS(a) [g]']) || 0;

            return {
              time: timeKey,
              A_x: Math.round(A_x * 1000) / 1000,
              A_y: Math.round(A_y * 1000) / 1000,
              A_z: Math.round(A_z * 1000) / 1000,
              A_tot: Math.round(A_tot * 1000) / 1000
            };
          });

          // Sort by time
          minuteData.sort((a, b) => {
            const [aHour, aMin] = a.time.split(':').map(Number);
            const [bHour, bMin] = b.time.split(':').map(Number);
            return (aHour * 60 + aMin) - (bHour * 60 + bMin);
          });

          setImuData(minuteData);
        })
        .catch(err => console.error("Error loading vibration data:", err));
    } else {
      // Old format processing using centralized API service
      const files = imuDates.find(d => d.value === selectedDate)?.files || [];

      Promise.all(files.map(f =>
        fetchIMUData(f.filename)
      )).then(results => {
        // Create full time range from 00:00 to 23:00
        const fullTimeRange = [];
        for (let hour = 0; hour < 24; hour++) {
          fullTimeRange.push(`${hour.toString().padStart(2, '0')}:00`);
        }

        // Initialize hourly data buckets
        const hourlyBuckets = {};
        fullTimeRange.forEach(time => {
          hourlyBuckets[time] = {
            x: [],
            y: [],
            z: [],
            total: []
          };
        });

        results.forEach(data => {
          const acc = data.values["N-linear-acc"];
          const rate = data.rate;
          const start = dayjs(data.start, "YYYY-MM-DD_HH-mm-ss");

          acc.forEach((v, i) => {
            // utc +2
            const timestamp = start.add(i / rate, "seconds").add(2, "hours");
            const hourKey = `${timestamp.hour().toString().padStart(2, '0')}:00`;

            const A_tot = Math.sqrt(v[0] ** 2 + v[1] ** 2 + v[2] ** 2);

            if (hourlyBuckets[hourKey]) {
              hourlyBuckets[hourKey].x.push(Math.abs(v[0]));
              hourlyBuckets[hourKey].y.push(Math.abs(v[1]));
              hourlyBuckets[hourKey].z.push(Math.abs(v[2]));
              hourlyBuckets[hourKey].total.push(A_tot);
            }
          });
        });

        // Calculate hourly averages
        const hourlyData = fullTimeRange.map(time => {
          const bucket = hourlyBuckets[time];

          const avg = (arr) => arr.length > 0
            ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 1000) / 1000
            : null;

          return {
            time,
            A_x: avg(bucket.x),
            A_y: avg(bucket.y),
            A_z: avg(bucket.z),
            A_tot: avg(bucket.total)
          };
        });

        setImuData(hourlyData);
      });
    }
  }, [selectedDate, imuDates, useNewFormat]);

  // Check if only radar is selected (no road data)
  const hasRoadData = arco && arco.length > 0;

  // Group roads by hash (similar to Proprieta component)
  let roads = [];
  if (hasRoadData && Array.isArray(arco)) {
    const roadsByHash = {};
    arco.forEach(element => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    // Get unique roads
    roads = Object.keys(roadsByHash).map(hash => ({
      hash,
      elements: roadsByHash[hash],
      data: roadsByHash[hash][roadsByHash[hash].length - 1]
    }));
  }

  // Check if radar is currently selected
  const isRadarView = radarSelected && selectedRoadIndex === roads.length;

  // Render chart component
  const renderChart = (height = 250) => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={imuData}>
        <defs>
          <linearGradient id="colorImuTotal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8C74FF" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#8C74FF" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorImuX" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#FF4D4D" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#FF4D4D" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorImuY" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#4ECDC4" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#4ECDC4" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorImuZ" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#FFE100" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#FFE100" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="time"
          tick={{ fontSize: 11, fill: '#666' }}
          axisLine={{ stroke: '#e0e0e0' }}
          interval="preserveStartEnd"
          ticks={TIME_TICKS}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#666' }}
          axisLine={{ stroke: '#e0e0e0' }}
          domain={['dataMin', 'dataMax']}
          label={{ value: 'g', position: 'insideLeft', style: { fontSize: 12, fill: '#666' } }}
        />
        <Tooltip
          formatter={(value, name) => {
            const axisName =
              name === 'A_tot' ? 'Totale' :
              name === 'A_x' ? 'Asse X' :
              name === 'A_y' ? 'Asse Y' :
              name === 'A_z' ? 'Asse Z' : name;
            return [`${value} g`, axisName];
          }}
          contentStyle={{ backgroundColor: '#f9f9f9', border: '1px solid #d9d9d9' }}
        />
        {selectedAxes.length > 1 && <Legend />}
        {selectedAxes.includes('total') && (
          <Area
            type="monotone"
            dataKey="A_tot"
            stroke="#8C74FF"
            strokeWidth={2}
            fill="url(#colorImuTotal)"
            name="Totale"
            connectNulls={true}
          />
        )}
        {selectedAxes.includes('x') && (
          <Area
            type="monotone"
            dataKey="A_x"
            stroke="#FF4D4D"
            strokeWidth={2}
            fill="url(#colorImuX)"
            name="Asse X"
            connectNulls={true}
          />
        )}
        {selectedAxes.includes('y') && (
          <Area
            type="monotone"
            dataKey="A_y"
            stroke="#4ECDC4"
            strokeWidth={2}
            fill="url(#colorImuY)"
            name="Asse Y"
            connectNulls={true}
          />
        )}
        {selectedAxes.includes('z') && (
          <Area
            type="monotone"
            dataKey="A_z"
            stroke="#FFE100"
            strokeWidth={2}
            fill="url(#colorImuZ)"
            name="Asse Z"
            connectNulls={true}
          />
        )}
        <Brush
          dataKey="time"
          height={30}
          stroke="#8C74FF"
          fill="#f5f5f5"
          travellerWidth={10}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  if (loadingData) {
    return null;
  }

  return (
    <>
    <div className="imu-wrapper">
      <div className="proprieta-title">
        <img src={VibrazioneIcon} alt="Congestion" className="proprieta-icon-img" />
        <h3>Vibrazione</h3>
      </div>
      <div className="proprieta-content anomaly-content">
        <p className="proprieta-description anomaly-description">
          Seleziona la data per la quale vuoi visualizzare i valori.
        </p>
        <Select
          placeholder="Seleziona data"
          className="anomaly-date-picker"
          value={selectedDate}
          onChange={setSelectedDate}
          getPopupContainer={(trigger) => trigger.parentElement}
          popupStyle={{ zIndex: 9999 }}
          style={{ borderRadius: "20px" }}
        >
          {imuDates.map(d => (
            <Select.Option key={d.value} value={d.value}>
              {d.value && d.value.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(d.value).format('DD/MM/YYYY') : d.value}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="imu-content">
        {/* Axis Selection Buttons */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#333' }}>
            Seleziona assi da visualizzare (multipla selezione):
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => toggleAxis('total')}
              style={{
                padding: '8px 16px',
                border: selectedAxes.includes('total') ? '2px solid #8C74FF' : '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: selectedAxes.includes('total') ? '#F0EDFF' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selectedAxes.includes('total') ? 600 : 400,
                color: selectedAxes.includes('total') ? '#8C74FF' : '#666',
                transition: 'all 0.2s'
              }}
            >
              {selectedAxes.includes('total') ? '✓ ' : ''}Totale (√(X²+Y²+Z²))
            </button>
            <button
              onClick={() => toggleAxis('x')}
              style={{
                padding: '8px 16px',
                border: selectedAxes.includes('x') ? '2px solid #FF4D4D' : '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: selectedAxes.includes('x') ? '#FFE6E6' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selectedAxes.includes('x') ? 600 : 400,
                color: selectedAxes.includes('x') ? '#FF4D4D' : '#666',
                transition: 'all 0.2s'
              }}
            >
              {selectedAxes.includes('x') ? '✓ ' : ''}Asse X
            </button>
            <button
              onClick={() => toggleAxis('y')}
              style={{
                padding: '8px 16px',
                border: selectedAxes.includes('y') ? '2px solid #4ECDC4' : '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: selectedAxes.includes('y') ? '#E6F9F7' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selectedAxes.includes('y') ? 600 : 400,
                color: selectedAxes.includes('y') ? '#4ECDC4' : '#666',
                transition: 'all 0.2s'
              }}
            >
              {selectedAxes.includes('y') ? '✓ ' : ''}Asse Y
            </button>
            <button
              onClick={() => toggleAxis('z')}
              style={{
                padding: '8px 16px',
                border: selectedAxes.includes('z') ? '2px solid #FFE100' : '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: selectedAxes.includes('z') ? '#FFFBE6' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: selectedAxes.includes('z') ? 600 : 400,
                color: selectedAxes.includes('z') ? '#FFE100' : '#666',
                transition: 'all 0.2s'
              }}
            >
              {selectedAxes.includes('z') ? '✓ ' : ''}Asse Z
            </button>
          </div>
        </div>

        {/* Road/Radar Selector if multiple elements */}
        {(roads.length > 1 || (roads.length >= 1 && radarSelected)) && (
          <div className="road-selector" style={{ marginBottom: '16px' }}>
            <p className="road-selector-label" style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#333' }}>
              Seleziona elemento da visualizzare:
            </p>
            <div className="road-selector-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {roads.map((road, index) => (
                <button
                  key={road.hash}
                  className={`road-btn ${selectedRoadIndex === index ? 'active' : ''}`}
                  style={{
                    padding: '8px 16px',
                    border: selectedRoadIndex === index ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: selectedRoadIndex === index ? '#e6f7ff' : '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: selectedRoadIndex === index ? 600 : 400,
                    color: selectedRoadIndex === index ? '#1890ff' : '#666',
                    transition: 'all 0.3s'
                  }}
                  onClick={() => setSelectedRoadIndex(index)}
                >
                  Strada {index + 1}
                  <span style={{ display: 'block', fontSize: '11px', marginTop: '2px', opacity: 0.8 }}>
                    {road.elements.length} punti
                  </span>
                </button>
              ))}
              {radarSelected && (
                <button
                  className={`road-btn ${isRadarView ? 'active' : ''}`}
                  style={{
                    padding: '8px 16px',
                    border: isRadarView ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: isRadarView ? '#e6f7ff' : '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: isRadarView ? 600 : 400,
                    color: isRadarView ? '#1890ff' : '#666',
                    transition: 'all 0.3s'
                  }}
                  onClick={() => setSelectedRoadIndex(roads.length)}
                >
                  Radar
                  <span style={{ display: 'block', fontSize: '11px', marginTop: '2px', opacity: 0.8 }}>
                    Stazione ambientale
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Display IMU data for both roads and radar */}
            {imuData.length > 0 ? (
              <div className="imu-chart-container">
                <ExpandButton onClick={() => setIsExpanded(true)} />
                {renderChart()}
              </div>
            ) : selectedDate ? (
              <div className="imu-no-data">
                <p>Nessun dato disponibile per questa data.</p>
              </div>
            ) : (
              <div className="imu-no-data">
                <p>Seleziona una data per visualizzare l'andamento delle vibrazioni.</p>
              </div>
            )}
      </div>
    </div>

    {/* Expanded Chart Modal */}
    <Modal
      open={isExpanded}
      onCancel={() => setIsExpanded(false)}
      footer={null}
      width="95%"
      centered
      title="Vibrazione (IMU) - Vista Espansa"
      destroyOnClose
      zIndex={9999}
    >
      <div className="imu-modal-content">
        {renderChart(500)}
      </div>
    </Modal>
    </>
  );
};

export default IMU;

/*
===========================================
📊 OTTIMIZZAZIONE COMPONENTE IMU - RESOCONTO
===========================================

📈 MODIFICHE IMPLEMENTATE:

STEP 1 - FILTRO FASCIA ORARIA ⏰
• Problema: 24h di dati sovraccaricano il grafico
• Soluzione: Filtro solo 8:00-18:00 (fascia lavorativa)
• Risultato: Riduzione da 24h a 10h di dati (-60% timespan)

STEP 2 - CAMPIONAMENTO AGGRESSIVO 📊  
• Problema: Troppi punti anche con filtro ogni 10°
• Soluzione: Campionamento ogni 100° punto invece di 10°
• Risultato: Riduzione 10x dei punti visualizzati

STEP 3 - INFO FILTRI APPLICATI 🔧
• Aggiunta card informativa sui filtri attivi
• Mostra: fascia oraria, campionamento, punti totali
• Trasparenza per l'utente sui dati visualizzati

STEP 4 - MIGLIORAMENTI VISUALIZZAZIONE 🎨
• Tooltip migliorato con formato data completo
• Y-axis con dominio dinamico (dataMin/dataMax)
• Line più spessa (strokeWidth: 1.5) per visibilità
• Stile tooltip personalizzato con bordi
===========================================
*/
